import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Award, UserCheck, Star, Target, Trophy, Gem } from 'lucide-react'
import SEO from '../../components/SEO'

const WhiteGloveService = () => {
  const features = [
    {
      icon: Award,
      title: 'Elite Service Standards',
      description: 'The highest level of service delivery with meticulous attention to every detail.'
    },
    {
      icon: UserCheck,
      title: 'Dedicated Team',
      description: 'A handpicked team of experts exclusively focused on your technology ecosystem.'
    },
    {
      icon: Target,
      title: 'Tailored Solutions',
      description: 'Completely customized technology solutions designed specifically for your needs.'
    },
    {
      icon: Trophy,
      title: 'Premium Experience',
      description: 'Exceptional service that exceeds expectations at every touchpoint.'
    },
    {
      icon: Star,
      title: 'Priority Access',
      description: 'First priority status for all services, support, and new technology offerings.'
    },
    {
      icon: Gem,
      title: 'Exclusive Benefits',
      description: 'Access to exclusive technology partnerships and early access to innovations.'
    }
  ]

  const services = [
    {
      title: 'Personalized Technology Strategy',
      description: 'Custom technology roadmap aligned with your lifestyle and business objectives.'
    },
    {
      title: 'Concierge Setup & Configuration',
      description: 'Expert setup of all devices and systems with your preferences perfectly configured.'
    },
    {
      title: 'Premium Support',
      description: 'Direct access to senior engineers via phone, email, text, or video at any time.'
    },
    {
      title: 'Proactive Monitoring',
      description: 'Continuous monitoring of your technology environment with preventative maintenance.'
    },
    {
      title: 'Quarterly Technology Reviews',
      description: 'Regular consultations to ensure your technology remains cutting-edge and secure.'
    },
    {
      title: 'VIP Vendor Relations',
      description: 'We manage all vendor relationships on your behalf with expedited service levels.'
    }
  ]

  return (
    <>
      <SEO 
        title="White Glove Service - Praetorian Works"
        description="Premium white glove technology services for discerning HNW and UHNW clients. Elite support, dedicated teams, and exceptional service standards."
        keywords="white glove IT service, premium technology support, elite tech services, luxury IT management, VIP technology service"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl">
                <Award className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                White Glove
                <span className="text-primary-500 ml-4">Service</span>
              </h1>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                Experience technology service at its finest. Meticulous attention to detail, 
                personalized care, and the highest standards of excellence in every interaction.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Features */}
        <section className="py-20">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-gradient-to-br from-secondary-800 to-secondary-900 rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg">
                    <feature.icon className="w-8 h-8 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {feature.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-5xl mx-auto"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
                Premium <span className="text-primary-500">Service Portfolio</span>
              </h2>
              <div className="space-y-6">
                {services.map((service, index) => (
                  <motion.div
                    key={service.title}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 * index }}
                    className="bg-secondary-800 rounded-xl p-8 border border-neutral-800 hover:border-primary-500/30 transition-all duration-300"
                  >
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {service.title}
                    </h3>
                    <p className="text-neutral-400 text-lg leading-relaxed">
                      {service.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Commitment */}
        <section className="py-20">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Our Commitment to <span className="text-primary-500">Excellence</span>
              </h2>
              <p className="text-xl text-neutral-300 mb-12 leading-relaxed">
                White Glove Service represents our unwavering commitment to providing the highest 
                caliber of technology support. Every interaction reflects our dedication to your 
                satisfaction and success. We don't just meet expectations—we exceed them.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-black/30">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Experience the Difference
              </h2>
              <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
                Discover what white glove service truly means. Schedule a consultation to 
                learn how we can elevate your technology experience.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Begin Your Journey
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default WhiteGloveService
